/**
 * Overview Tab — May 2026 rev3 per user feedback.
 *
 * Changes vs rev2:
 *  • Customer card is now editable (name / email / city). Mobile read-only.
 *  • Layout: row1 (Customer + Vehicle) · row2 (Service Location + Slot) ·
 *    row3 (Package & Pricing full-width with current→new diff).
 *  • Per-section inline Save/Cancel buttons removed — sections now report
 *    dirty drafts up to the modal via onDirtyChange(). Footer "Save All
 *    Changes" commits everything in one PATCH.
 *  • Slot section gains a Status dropdown (BOOKED / CONFIRMED / ASSIGNED /
 *    IN_PROGRESS / RESCHEDULED / COMPLETED / CANCELLED / REFUND_REQUESTED).
 *  • Package & Pricing shows current + new package side-by-side with the
 *    customer's payable price difference (more / less).
 */
import React, { useEffect, useState } from "react";
import { MapPin, ExternalLink, Search, Loader2, Phone, MessageCircle, User, Wrench } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { PlacesAutocomplete } from "../../ui/PlacesAutocomplete";
import { serviceOpsApi } from "../../../services/api";
import { toast } from "sonner";
import {
  formatDate, formatTime, formatDateTime,
  toDateTimeLocalInput,
} from "../../../utils/datetime";
import { normaliseCity } from "../../../utils/cityAlias";
import {
  useStatusCatalogue, statusesIn, reasonsFor, requiresReason,
} from "../useStatusCatalogue";

// Fallback only — the real list comes from the backend catalogue
// (GET /api/service-ops/status-catalogue), which also drives the page filter
// and the KPI cards so the three can never drift apart again.
// Statuses that ops can pick from the Schedule section dropdown.
// (Sales-only statuses like CONTACTED/QUOTED are excluded — they belong on
// the Service Leads page, not Service Ops.)
const STATUS_OPTIONS = [
  { value: "BOOKED",                  label: "Booked" },
  { value: "CONFIRMED",               label: "Confirmed" },
  { value: "ASSIGNED",                label: "Mechanic Assigned" },
  { value: "IN_PROGRESS",             label: "Service In Progress" },
  { value: "COMPLETED",               label: "Completed" },
  { value: "RESCHEDULED_BY_CUSTOMER", label: "Rescheduled by Customer" },
  { value: "RESCHEDULED_BY_WD",       label: "Rescheduled by Wisedrive" },
  { value: "CANCELLED_BY_CUSTOMER",   label: "Cancelled by Customer" },
  { value: "CANCELLED_BY_WD",         label: "Cancelled by Wisedrive" },
  { value: "REFUND_REQUESTED",        label: "Refund Requested" },
];

const Card = ({ title, action, children, testid, className = "" }) => (
  <div className={`border rounded-lg overflow-hidden bg-white shadow-sm ${className}`} data-testid={testid}>
    <div className="bg-slate-50 px-4 py-2 border-b flex items-center justify-between">
      <h3 className="text-sm font-semibold text-slate-700 tracking-tight">{title}</h3>
      {action}
    </div>
    <div className="p-4 text-sm">{children}</div>
  </div>
);

const ReadRow = ({ label, value }) => (
  <div className="flex justify-between items-start gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
    <span className="text-slate-500 text-xs uppercase tracking-wide font-medium">{label}</span>
    <span className="text-slate-900 font-medium text-right">{value || "—"}</span>
  </div>
);

// ─── Currently-Assigned Mechanic card ─────────────────────────────────────
// Renders contact + assignment-context info when a mechanic IS on the job.
// Renders nothing when assigned_mechanic_id is empty (the dropdown below
// is the only thing visible in that state).
//
// (Lifecycle TIMELINE — status_history + clock-in/out — lives in the
//  Activity tab, NOT here, to avoid duplicating the same data twice.)
function AssignedMechanicCard({ lead, mechanic }) {
  if (!lead?.assigned_mechanic_id) return null;

  // Prefer the freshly-loaded mechanic doc (richer fields); fall back to the
  // denormalised fields embedded on the lead. Either path always renders.
  const name = mechanic?.name   || lead?.assigned_mechanic_name   || "Unknown mechanic";
  const mob  = mechanic?.mobile || lead?.assigned_mechanic_mobile || "";
  const city = mechanic?.city   || lead?.assigned_mechanic_city   || "";
  const assignedBy = lead?.assigned_by_name || lead?.assigned_by_user || null;
  const assignedAt = lead?.mechanic_assigned_at
    || (lead?.status_history || []).slice().reverse().find(h => h?.to === "ASSIGNED")?.at
    || null;

  const waHref   = mob ? `https://wa.me/${String(mob).replace(/[^\d]/g, "")}` : null;
  const callHref = mob ? `tel:${mob}` : null;

  return (
    <div
      data-testid="assigned-mechanic-card"
      className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2"
    >
      <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 uppercase tracking-wide">
        <Wrench className="h-3.5 w-3.5" />
        Currently Assigned
      </div>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <User className="h-4 w-4 text-emerald-600" />
            {name}
          </div>
          {city && (
            <div className="text-xs text-slate-600 flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {city}
            </div>
          )}
          {mob && (
            <div className="text-xs text-slate-600 flex items-center gap-1">
              <Phone className="h-3.5 w-3.5" />
              <span className="font-mono">{mob}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {callHref && (
            <a href={callHref} className="inline-flex items-center justify-center gap-1 text-xs px-2 py-1 rounded border bg-white hover:bg-slate-50 text-slate-700">
              <Phone className="h-3 w-3" /> Call
            </a>
          )}
          {waHref && (
            <a href={waHref} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1 text-xs px-2 py-1 rounded border bg-white hover:bg-emerald-100 text-emerald-700 border-emerald-300">
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </a>
          )}
        </div>
      </div>
      {(assignedBy || assignedAt) && (
        <div className="text-[11px] text-slate-500 border-t border-emerald-100 pt-1.5">
          {assignedBy && <>Assigned by <span className="font-medium text-slate-700">{assignedBy}</span></>}
          {assignedBy && assignedAt && " · "}
          {assignedAt && <>{formatDateTime(assignedAt)}</>}
        </div>
      )}
    </div>
  );
}

// ─── End of Schedule-section helpers ──────────────────────────────────────


const EditRow = ({ label, children }) => (
  <div className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
    <span className="text-slate-500 text-xs uppercase tracking-wide font-medium w-24 shrink-0">{label}</span>
    <div className="flex-1">{children}</div>
  </div>
);

export default function OverviewTab({ lead, user, mechanics, draft, setDraft }) {
  // ── Lift draft state to the modal level via props
  // (modal manages `draft` so the footer "Save All Changes" can flush it).
  const d = draft || {};
  const patch = (changes) => setDraft({ ...d, ...changes });

  // ── Location (no inline map preview — only an "Open Map" button)
  const sl = lead?.service_location || {};
  const addressVal = d.address ?? lead?.address ?? sl.address ?? "";
  const landmarkVal = d.landmark ?? sl.landmark ?? "";
  const coordsVal = d.coords ?? {
    lat: sl.latitude || null, lng: sl.longitude || null,
  };
  const mapsLink = coordsVal.lat && coordsVal.lng
    ? `https://maps.google.com/?q=${coordsVal.lat},${coordsVal.lng}`
    : addressVal ? `https://maps.google.com/?q=${encodeURIComponent(addressVal)}` : null;

  // ── Slot
  const requestedSlot = lead?.preferred_slot;
  const confirmedSlotVal = d.confirmed_slot ?? lead?.confirmed_slot ?? "";
  const statusVal = d.status ?? lead?.status ?? "BOOKED";

  // Status vocabulary + cancellation reasons from the server catalogue.
  const catalogue = useStatusCatalogue();
  const settableStatuses = [
    ...statusesIn(catalogue, "active"),
    ...statusesIn(catalogue, "closed"),
  ].filter((s) => s.ops_settable);
  const statusOptions = settableStatuses.length
    ? settableStatuses.map((s) => ({ value: s.value, label: s.label }))
    : STATUS_OPTIONS;
  // A cancel / refund move cannot be saved without a reason — the backend
  // rejects it too, this just says so before the user hits Save.
  const needsReason = statusVal !== (lead?.status || "")
    && requiresReason(catalogue, statusVal);
  const reasonOptions = reasonsFor(catalogue, statusVal);

  const v = lead?.vehicle || {};

  return (
    <div className="p-5 space-y-3" data-testid="overview-tab">
      {/* ───── Row 1: Customer + Service Location ───── */}
      <div className="grid grid-cols-2 gap-3">
        <Card title="Customer" testid="overview-customer">
          <EditRow label="Name">
            <Input
              value={d.customer_name ?? lead?.customer_name ?? ""}
              onChange={(e) => patch({ customer_name: e.target.value })}
              data-testid="customer-name-input"
            />
          </EditRow>
          <EditRow label="Mobile">
            <Input value={lead?.mobile || ""} disabled data-testid="customer-mobile-input" />
          </EditRow>
          <EditRow label="Email">
            <Input
              type="email"
              value={d.email ?? lead?.email ?? ""}
              onChange={(e) => patch({ email: e.target.value })}
              data-testid="customer-email-input"
            />
          </EditRow>
          <EditRow label="City">
            <Input
              value={d.city ?? lead?.city ?? ""}
              onChange={(e) => patch({ city: e.target.value })}
              data-testid="customer-city-input"
            />
          </EditRow>
        </Card>

        <Card
          title="Service Location"
          testid="overview-location"
          action={
            mapsLink ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => window.open(mapsLink, "_blank", "noopener")}
                data-testid="overview-open-map"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                Open Map
              </Button>
            ) : null
          }
        >
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Address</Label>
              <PlacesAutocomplete
                value={addressVal}
                onChange={(val) => patch({ address: val })}
                onSelect={(place) => {
                  patch({
                    address: place.address || addressVal,
                    coords: place.latitude && place.longitude
                      ? { lat: place.latitude, lng: place.longitude }
                      : coordsVal,
                  });
                }}
                placeholder="House #, street, area, city..."
                country={(lead?.country_code || "in").toLowerCase()}
              />
            </div>
            <div>
              <Label className="text-xs">Landmark</Label>
              <Input
                value={landmarkVal}
                onChange={(e) => patch({ landmark: e.target.value })}
                placeholder="e.g. Near Forum Mall"
                data-testid="location-landmark-input"
              />
            </div>
            {coordsVal.lat && coordsVal.lng ? (
              <div className="text-xs text-slate-500 font-mono">
                <MapPin className="h-3 w-3 inline mr-1" />
                {coordsVal.lat.toFixed(5)}, {coordsVal.lng.toFixed(5)}
              </div>
            ) : (
              <div className="text-xs text-slate-400 italic">
                <MapPin className="h-3 w-3 inline mr-1" />
                Pick a suggestion to capture lat/lng for the mechanic's GPS.
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ───── Row 2: Vehicle + Schedule (renamed from Slot) ───── */}
      <div className="grid grid-cols-2 gap-3">
        <Card title="Vehicle" testid="overview-vehicle">
          <VehicleEditor lead={lead} d={d} patch={patch} />
        </Card>

        <Card title="Schedule" testid="overview-slot">
          <div className="space-y-3">
            <EditRow label="Requested">
              <div className="text-slate-700">
                {requestedSlot ? (
                  <>
                    <span className="font-medium">{formatDate(requestedSlot)}</span>
                    <span className="text-slate-500 ml-2">{formatTime(requestedSlot)}</span>
                  </>
                ) : (
                  <span className="text-slate-400 italic">—</span>
                )}
              </div>
            </EditRow>
            <EditRow label="Confirmed">
              <input
                type="datetime-local"
                value={toDateTimeLocalInput(confirmedSlotVal)}
                onChange={(e) =>
                  patch({
                    confirmed_slot: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
                className="border rounded px-2 py-1 text-sm w-full"
                data-testid="slot-datetime-input"
              />
            </EditRow>
            <EditRow label="Status">
              <select
                className="w-full border rounded-md px-2 py-1.5 text-sm bg-white text-slate-900 cursor-pointer hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={statusOptions.some((s) => s.value === statusVal) ? statusVal : ""}
                onChange={(e) => patch({ status: e.target.value, reason_code: "", reason_note: "" })}
                data-testid="status-select"
                style={{ WebkitAppearance: "menulist", appearance: "menulist" }}
              >
                {!statusOptions.some((s) => s.value === statusVal) && (
                  <option value="" disabled>{statusVal || "-- pick a status --"}</option>
                )}
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </EditRow>

            {needsReason && (
              <div
                className="rounded-md border border-rose-200 bg-rose-50/70 p-2.5 space-y-2"
                data-testid="status-reason-block"
              >
                <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">
                  Reason required
                </div>
                <select
                  className="w-full border rounded-md px-2 py-1.5 text-sm bg-white text-slate-900"
                  value={d.reason_code || ""}
                  onChange={(e) => patch({ reason_code: e.target.value })}
                  data-testid="status-reason-select"
                  style={{ WebkitAppearance: "menulist", appearance: "menulist" }}
                >
                  <option value="">-- pick a reason --</option>
                  {reasonOptions.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </select>
                <Input
                  value={d.reason_note || ""}
                  onChange={(e) => patch({ reason_note: e.target.value })}
                  placeholder={d.reason_code === "OTHER"
                    ? "Required — what exactly happened?"
                    : "Optional note"}
                  data-testid="status-reason-note"
                />
                <div className="text-[11px] text-rose-700/80">
                  This job moves to the “Cancelled &amp; Refunds” tab, with the
                  amount collected and the Razorpay payment id shown for refund.
                </div>
              </div>
            )}

            {/* ── Currently-Assigned card ─ shows when mechanic is on the job ── */}
            <AssignedMechanicCard
              lead={lead}
              mechanic={(mechanics || []).find(m => m.id === (d.mechanic_id ?? lead?.assigned_mechanic_id))}
            />

            <EditRow label={lead?.assigned_mechanic_id ? "Change Mechanic" : "Assign Mechanic"}>
              <select
                className="w-full border rounded-md px-2 py-1.5 text-sm bg-white text-slate-900 cursor-pointer hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={d.mechanic_id ?? lead?.assigned_mechanic_id ?? ""}
                onChange={(e) => patch({ mechanic_id: e.target.value })}
                data-testid="mechanic-select"
                style={{ WebkitAppearance: "menulist", appearance: "menulist" }}
              >
                <option value="">-- unassigned --</option>
                {(() => {
                  // Filter mechanics by the lead's service city, normalising
                  // both sides via the city-alias util so "Bengalore" (typo),
                  // "Bangalore" (old name), and "Bengaluru" (new name) all
                  // resolve to the same canonical key.
                  //
                  // Per product directive (2026-05-28): NEVER offer mechanics
                  // from other cities in this dropdown — a Bengaluru job must
                  // not be reassigned to a Chennai/Pune/Mumbai mechanic by
                  // accident. The historical/already-assigned mechanic is
                  // surfaced separately by <AssignedMechanicCard> above, so
                  // dropping the cross-city options here doesn't hide it.
                  const leadCityRaw = (
                    lead?.service_location?.city
                    || lead?.city
                    || lead?.customer_city
                    || ""
                  );
                  const leadCityKey = normaliseCity(leadCityRaw);
                  const all = mechanics || [];
                  const matchesCity = (m) => {
                    if (!leadCityKey) return true;
                    if (normaliseCity(m.city) === leadCityKey) return true;
                    // `inspection_cities` is what the HR city picker actually
                    // writes — 14 of 28 active mechanics have ONLY that field,
                    // which is why correctly-configured mechanics (Nitin,
                    // Sami Automobile Mumbai…) never appeared here.
                    const arr = [
                      ...(m.assigned_cities || []),
                      ...(m.inspection_cities || []),
                    ].map(normaliseCity);
                    return arr.includes(leadCityKey);
                  };
                  const cityMatched = all.filter(m => matchesCity(m));
                  const cityLabel = leadCityRaw || "this city";
                  return (
                    <>
                      {cityMatched.length > 0 ? (
                        <optgroup label={`📍 ${cityLabel} (${cityMatched.length})`}>
                          {cityMatched.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}{(m.city || (m.inspection_cities || [])[0]) ? ` · ${m.city || (m.inspection_cities || [])[0]}` : ""}{m.mobile ? ` · ${m.mobile}` : ""}
                            </option>
                          ))}
                        </optgroup>
                      ) : (
                        <option value="" disabled>
                          {leadCityRaw
                            ? `-- no active mechanics in ${leadCityRaw} --`
                            : "-- no active mechanics found --"}
                        </option>
                      )}
                    </>
                  );
                })()}
              </select>
            </EditRow>
            <div className="text-[11px] text-slate-500 italic">
              Saving with a different mechanic will auto-WhatsApp them with
              customer + car + slot + map link.
            </div>
          </div>
        </Card>
      </div>

      {/* Package & Pricing moved to the "Pricing & Spares" tab — keeps the
          Overview surface focused on Customer / Vehicle / Location / Slot. */}

      <div className="text-xs text-slate-400 pt-1">
        Created {formatDateTime(lead?.created_at)} · Updated {formatDateTime(lead?.updated_at)}
      </div>
    </div>
  );
}

/**
 * Vehicle editor — editable Brand / Model / Fuel / RC No. / Year / KMs.
 * Includes a "Lookup by RC" button that hits Surepass (24h cached via the
 * backend's `public_vehicle_cache` so we don't burn API credits on repeat
 * RC numbers). Filled fields auto-populate Brand, Model, Fuel and Year.
 * All edits route into the draft.vehicle object — flushed by Save All
 * Changes on the modal footer.
 */
function VehicleEditor({ lead, d, patch }) {
  const v = lead?.vehicle || {};
  // Local draft on top of the lead's vehicle. Anything in `d.vehicle`
  // overrides; otherwise falls back to the lead's persisted values.
  const dv = d.vehicle || {};
  // get() handles BOTH new-schema (`manufacture_year`, `kms_driven`,
  // `rc_number`) AND the legacy/Surepass field names actually persisted
  // in prod (`year`, `kms`, `rc_no`). When user edits, we always write to
  // the new-schema key — but reads must fall back gracefully so already-
  // populated leads don't appear blank.
  const FALLBACK_KEYS = {
    manufacture_year: ["year"],
    kms_driven: ["kms"],
    rc_number: ["rc_no"],
  };
  const get = (k, fallback = "") => {
    if (dv[k] !== undefined && dv[k] !== null) return dv[k];
    if (v[k] !== undefined && v[k] !== null) return v[k];
    for (const alt of (FALLBACK_KEYS[k] || [])) {
      if (dv[alt] !== undefined && dv[alt] !== null) return dv[alt];
      if (v[alt] !== undefined && v[alt] !== null) return v[alt];
    }
    return fallback;
  };
  const setField = (k, val) => patch({ vehicle: { ...v, ...dv, [k]: val } });

  const [rcInput, setRcInput] = useState(get("rc_number"));
  const [looking, setLooking] = useState(false);

  // Keep the RC input synced if the lead reloads.
  useEffect(() => { setRcInput(get("rc_number")); /* eslint-disable-next-line */ }, [lead?.id]);

  const onLookup = async () => {
    const reg = (rcInput || "").toUpperCase().replace(/\s+/g, "").trim();
    if (reg.length < 4) {
      toast.error("Enter a valid registration number");
      return;
    }
    setLooking(true);
    try {
      const { data } = await serviceOpsApi.lookupRc(reg);
      const veh = data?.vehicle || {};
      // Merge fetched fields into the draft. Don't clobber existing manual
      // edits with blanks, AND don't overwrite a real brand with the
      // Surepass junk placeholder "OTHERS" (which slips through for older
      // vehicles even though we try to derive it server-side).
      const JUNK = new Set(["others", "other", "n/a", "na", "---", "-",
                            "not available", "unknown"]);
      const isJunk = (val) => !val || JUNK.has(String(val).trim().toLowerCase());
      const merged = { ...v, ...dv };
      if (veh.rc_number) merged.rc_number = veh.rc_number;
      if (!isJunk(veh.brand)) merged.brand = veh.brand;
      if (veh.model) merged.model = veh.model;
      if (veh.fuel_type) merged.fuel_type = veh.fuel_type;
      if (veh.manufacture_year) merged.manufacture_year = veh.manufacture_year;
      if (veh.color) merged.color = veh.color;
      if (veh.variant) merged.variant = veh.variant;
      patch({ vehicle: merged });
      const brandLabel = merged.brand || "(brand unchanged)";
      const modelLabel = merged.model || "";
      toast.success(`Fetched from ${data?.source === "cache" ? "cache" : "Surepass"} · ${brandLabel} ${modelLabel}`.trim());
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error("Lookup failed: " + (typeof detail === "string" ? detail : JSON.stringify(detail)));
    } finally {
      setLooking(false);
    }
  };

  return (
    <div className="space-y-2">
      {/* RC + Lookup */}
      <EditRow label="RC No.">
        <div className="flex gap-2">
          <Input
            value={rcInput}
            onChange={(e) => {
              setRcInput(e.target.value);
              setField("rc_number", e.target.value.toUpperCase());
            }}
            placeholder="e.g. KA01AB1234"
            className="uppercase"
            data-testid="vehicle-rc-input"
          />
          <Button
            size="sm" variant="outline"
            onClick={onLookup} disabled={looking || !rcInput}
            data-testid="vehicle-rc-lookup-btn"
            title="Fetch from Surepass RTO"
          >
            {looking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </EditRow>

      <EditRow label="Brand">
        <Input value={get("brand")} onChange={(e) => setField("brand", e.target.value)}
               placeholder="Hyundai" data-testid="vehicle-brand-input" />
      </EditRow>

      <EditRow label="Model">
        <Input value={get("model")} onChange={(e) => setField("model", e.target.value)}
               placeholder="Creta" data-testid="vehicle-model-input" />
      </EditRow>

      <EditRow label="Fuel">
        <select
          value={get("fuel_type")}
          onChange={(e) => setField("fuel_type", e.target.value)}
          className="w-full border rounded-md px-2 py-1.5 text-sm bg-white"
          data-testid="vehicle-fuel-input"
        >
          <option value="">— select —</option>
          {["Petrol", "Diesel", "CNG", "Electric", "Hybrid", "LPG"].map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </EditRow>

      <EditRow label="Year">
        <Input type="number" min={1980} max={2030}
               value={get("manufacture_year")}
               onChange={(e) => setField("manufacture_year", e.target.value ? Number(e.target.value) : null)}
               placeholder="2018"
               data-testid="vehicle-year-input" />
      </EditRow>

      <EditRow label="KMs Driven">
        <Input type="number" min={0}
               value={get("kms_driven")}
               onChange={(e) => setField("kms_driven", e.target.value ? Number(e.target.value) : null)}
               placeholder="45000"
               data-testid="vehicle-kms-input" />
      </EditRow>

      <div className="text-[11px] text-slate-500 italic">
        <Search className="h-3 w-3 inline mr-1" />
        Click the search icon next to RC No. to auto-fill Brand / Model / Fuel / Year from Surepass (cached 24h).
      </div>
    </div>
  );
}
